import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import SignatureInput from '../components/SignatureInput';
import { generateFaceToFacePDF, downloadPDF, printPDF } from '../utils/pdfExport';
import { cachePdfPatients } from '../utils/fillable';
import { FileText, Save, ArrowLeft, Download, Printer, User, Home, Stethoscope, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FaceToFaceForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    encounterDate: new Date().toISOString().split('T')[0],
    // Services
    skilledNursing: false,
    physicalTherapy: false,
    speechTherapy: false,
    occupationalTherapy: false,
    socialWork: false,
    homeHealthAide: false,
    dmeSupply: false,
    // Homebound Certification
    leavingHomeTaxingEffort: false,
    absencesInfrequent: false,
    medicallyRestricted: false,
    medicallyRestrictedReason: '',
    // Clinical Findings
    clinicalFindings: '',
    // Signature (comprehensive - typed, voice recording, handwritten)
    physicianSignature: { typed: '', audioURL: null, canvasDataURL: null }
  });

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients); cachePdfPatients(data.patients);
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) setFormData(prev => ({ ...prev, patientName: `${patient.firstName} ${patient.lastName}` }));
        }
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/face-to-face/${formId}`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) setFormData(data.form);
    } catch (error) { toast.error('Failed to load form'); }
    finally { setLoading(false); }
  };

  const handlePatientChange = (e) => {
    const patient = patients.find(p => p.id === e.target.value);
    if (patient) setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: `${patient.firstName} ${patient.lastName}` }));
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!formData.patientId) { toast.error('Please select a patient'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId ? `${API_URL}/api/forms/face-to-face/${formId}` : `${API_URL}/api/forms/face-to-face`;
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form) navigate(`/dashboard/forms/face-to-face?formId=${data.form.id}`);
      }
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard/documentation')} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#f39c12]" />Face to Face Encounter
                </h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  Physician Certification for Home Health Services
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                const doc = generateFaceToFacePDF(formData);
                downloadPDF(doc, `face-to-face-${formData.patientName || 'form'}.pdf`);
              }} data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
              <Button variant="outline" onClick={() => {
                const doc = generateFaceToFacePDF(formData);
                printPDF(doc);
              }} data-testid="print-pdf-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Patient Info */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />Patient Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Patient *</Label>
              <select value={formData.patientId} onChange={handlePatientChange}
                className="w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#f39c12]">
                <option value="">Select patient</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
              </select>
            </div>
            <div>
              <Label>Encounter Date</Label>
              <Input type="date" value={formData.encounterDate} onChange={(e) => handleChange('encounterDate', e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>

        {/* Services Requested */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-green-600" />Home Health Services Requested
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { field: 'skilledNursing', label: 'Skilled Nursing' },
              { field: 'physicalTherapy', label: 'Physical Therapy' },
              { field: 'speechTherapy', label: 'Speech Therapy' },
              { field: 'occupationalTherapy', label: 'Occupational Therapy' },
              { field: 'socialWork', label: 'Medical Social Work' },
              { field: 'homeHealthAide', label: 'Home Health Aide' },
              { field: 'dmeSupply', label: 'DME/Supply' }
            ].map(({ field, label }) => (
              <Label key={field} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                {label}
              </Label>
            ))}
          </div>
        </div>

        {/* Homebound Certification */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-purple-600" />Homebound Certification
          </h2>
          <div className="bg-blue-50 p-4 rounded-lg mb-4 text-sm text-blue-800">
            <p className="font-medium mb-2">To qualify as homebound, the patient must meet BOTH conditions below:</p>
            <ol className="list-decimal pl-4 space-y-1">
              <li>Leaving home takes considerable and taxing effort</li>
              <li>Absences from home are infrequent and of short duration OR due to receiving health care</li>
            </ol>
          </div>
          <div className="space-y-4">
            <Label className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <Checkbox checked={formData.leavingHomeTaxingEffort} onCheckedChange={(c) => handleChange('leavingHomeTaxingEffort', c)} className="mt-1" />
              <div>
                <span className="font-medium">Leaving home takes considerable and taxing effort</span>
                <p className="text-sm text-gray-600 mt-1">Patient requires assistive device, assistance from another person, or has a condition that makes leaving home inadvisable</p>
              </div>
            </Label>
            <Label className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <Checkbox checked={formData.absencesInfrequent} onCheckedChange={(c) => handleChange('absencesInfrequent', c)} className="mt-1" />
              <div>
                <span className="font-medium">Absences from home are infrequent or for medical care</span>
                <p className="text-sm text-gray-600 mt-1">Leaves home rarely and for short duration, or primarily for health care treatment</p>
              </div>
            </Label>
            <Label className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <Checkbox checked={formData.medicallyRestricted} onCheckedChange={(c) => handleChange('medicallyRestricted', c)} className="mt-1" />
              <div>
                <span className="font-medium">Medically restricted from leaving home</span>
                <p className="text-sm text-gray-600 mt-1">Doctor's orders restrict patient from leaving home due to medical condition</p>
              </div>
            </Label>
            {formData.medicallyRestricted && (
              <div className="ml-7">
                <Label>Specify Restriction Reason</Label>
                <Input value={formData.medicallyRestrictedReason} onChange={(e) => handleChange('medicallyRestrictedReason', e.target.value)} 
                  className="mt-1" placeholder="Describe medical restriction" />
              </div>
            )}
          </div>
        </div>

        {/* Clinical Findings */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />Clinical Findings
          </h2>
          <div className="bg-yellow-50 p-4 rounded-lg mb-4 text-sm text-yellow-800">
            <p>Document the clinical findings from the face-to-face encounter that support the patient's need for home health services and homebound status.</p>
          </div>
          <VoiceDictationInput
            value={formData.clinicalFindings}
            onChange={(e) => handleChange('clinicalFindings', e.target.value)}
            rows={6}
            placeholder="Document clinical findings, primary diagnosis, functional limitations..."
          />
        </div>

        {/* Physician Certification */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />Physician Certification
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm">
            <p>I certify that I had a face-to-face encounter with this patient within the required timeframe and that the clinical findings of the encounter support the need for skilled home health services and the patient's homebound status.</p>
          </div>
          <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SignatureInput 
              label="Physician Signature" 
              value={formData.physicianSignature} 
              onChange={(value) => handleChange('physicianSignature', value)} 
            />
            <div>
              <Label>Date</Label>
              <Input type="date" value={formData.encounterDate} disabled className="mt-1 bg-gray-100" />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-8">
          <Button variant="outline" onClick={() => navigate('/dashboard/documentation')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save Form</>}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FaceToFaceForm;
